/**
 * Dire en français ce qui s'est passé, et quoi faire.
 *
 * Le 06/10/2026, un enregistrement refusé a affiché tel quel :
 * « Could not find the 'montant_max' column of 'treso_regles_classement' in
 * the schema cache ». Un message qu'on ne comprend pas ne dit pas quoi faire,
 * et laisse croire à une panne générale. Cette fonction est le passage obligé
 * entre une erreur technique et l'écran.
 *
 * Trois règles :
 *
 *  1. **Ce qui s'est passé, puis quoi faire.** « Le serveur ne répond pas » ne
 *     suffit pas ; « vérifiez la connexion, puis réessayez » est la moitié utile.
 *  2. **Les messages déjà écrits en français passent tels quels.** L'application
 *     en produit beaucoup (« Ce client est lié à des devis… ») : ils sont plus
 *     précis que toute traduction générique.
 *  3. **Un message inconnu n'est pas effacé.** Il est encadré d'une phrase
 *     française, et le détail technique reste, entre guillemets, pour qui devra
 *     comprendre la panne. Le cacher rendrait le diagnostic impossible.
 */

import { colonneAbsente } from './erreurs-supabase.js';

interface ErreurLisible {
  message: string;
  code: string | null;
}

function lire(e: unknown): ErreurLisible {
  if (typeof e === 'string') return { message: e, code: null };
  if (e !== null && typeof e === 'object') {
    const o = e as { message?: unknown; code?: unknown };
    return {
      message: typeof o.message === 'string' ? o.message : '',
      code: typeof o.code === 'string' ? o.code : null,
    };
  }
  return { message: '', code: null };
}

/** Une famille d'erreurs, reconnue au code PostgreSQL / PostgREST ou au texte. */
interface Famille {
  codes: readonly string[];
  motifs: readonly RegExp[];
  texte: string;
}

// L'ordre compte : la première famille qui reconnaît l'erreur l'emporte. La
// connexion passe en tête — sans réseau, tout le reste est sans objet.
const FAMILLES: readonly Famille[] = [
  {
    codes: [],
    motifs: [
      /failed to fetch/i,
      /networkerror/i,
      /load failed/i,
      /fetch failed/i,
      /network request failed/i,
      /err_internet_disconnected/i,
      /err_connection/i,
    ],
    texte: 'Le serveur ne répond pas. Vérifiez la connexion internet, puis réessayez.',
  },
  // ----- Connexion : les premiers messages qu'un salarié voit -----------------
  {
    codes: ['invalid_credentials'],
    motifs: [/invalid login credentials/i],
    texte: 'Adresse email ou mot de passe incorrect. Vérifiez-les, puis réessayez.',
  },
  {
    codes: ['email_not_confirmed'],
    motifs: [/email not confirmed/i],
    texte:
      'Cette adresse email n’a pas encore été confirmée. Ouvrez le lien reçu par email, ' +
      'puis reconnectez-vous.',
  },
  {
    codes: ['over_request_rate_limit', 'over_email_send_rate_limit'],
    motifs: [/rate limit/i, /for security purposes, you can only request/i, /too many requests/i],
    texte: 'Trop de tentatives d’affilée. Patientez une minute, puis réessayez.',
  },
  {
    codes: ['weak_password'],
    motifs: [/password should be at least/i, /weak password/i],
    texte: 'Mot de passe trop faible. Choisissez-en un plus long.',
  },
  {
    codes: ['same_password'],
    motifs: [/new password should be different/i],
    texte: 'Le nouveau mot de passe doit être différent de l’ancien.',
  },
  {
    codes: ['user_already_exists', 'email_exists'],
    motifs: [/user already registered/i, /already been registered/i],
    texte: 'Un compte existe déjà pour cette adresse email.',
  },
  {
    codes: ['PGRST301', 'PGRST302', 'PGRST303'],
    motifs: [/jwt expired/i, /invalid jwt/i, /jwt.*(malformed|invalid)/i, /refresh token/i],
    texte: 'Votre session a expiré. Reconnectez-vous, puis recommencez.',
  },
  {
    codes: ['42501'],
    motifs: [/permission denied/i, /row-level security/i],
    texte:
      'Vous n’avez pas les droits pour cette action. Un administrateur peut vous les ' +
      'donner, dans Utilisateurs.',
  },
  {
    codes: ['57014'],
    motifs: [/statement timeout/i, /canceling statement/i, /timed? ?out/i],
    texte: 'Le serveur a mis trop de temps à répondre. Réessayez dans un instant.',
  },
  {
    codes: ['23505'],
    motifs: [/duplicate key/i],
    texte: 'Cet élément existe déjà. Cherchez-le plutôt que de le recréer.',
  },
  {
    codes: ['23503'],
    motifs: [/foreign key/i],
    texte:
      'Cet élément est utilisé ailleurs — un devis, un chantier ou une facture : ' +
      'impossible de le supprimer.',
  },
  {
    codes: ['23502'],
    motifs: [/null value in column/i, /violates not-null/i],
    texte: 'Un champ obligatoire est vide. Complétez-le, puis enregistrez à nouveau.',
  },
  {
    codes: ['23514'],
    motifs: [/violates check constraint/i],
    texte:
      'Une valeur saisie n’est pas acceptée — un montant, une date ou un statut hors ' +
      'des limites. Vérifiez la saisie.',
  },
];

const BASE_EN_RETARD =
  'Une mise à jour de la base est en attente sur le serveur. Rien n’est perdu ; ' +
  'prévenez l’administrateur pour qu’il l’applique.';

/** Le texte est-il déjà une phrase française écrite par l'application ? */
function semblePhraseFrancaise(texte: string): boolean {
  return (
    /[éèêëàâùûçôîï]/i.test(texte) ||
    /\b(le|la|les|de|des|du|un|une|est|sont|pas|aucun|aucune|impossible|veuillez)\b/i.test(texte)
  );
}

export function messageErreur(e: unknown): string {
  const { message, code } = lire(e);
  const texte = message.trim();

  // Une base en retard d'une migration : rien n'est cassé, il manque une étape.
  if (texte !== '' && colonneAbsente(texte)) return BASE_EN_RETARD;
  if (code === 'PGRST204' || code === 'PGRST205' || code === '42703' || code === '42P01') {
    return BASE_EN_RETARD;
  }

  for (const f of FAMILLES) {
    if (code !== null && f.codes.includes(code)) return f.texte;
    if (f.motifs.some((m) => m.test(texte))) return f.texte;
  }

  if (texte === '') return 'Une erreur inattendue s’est produite. Réessayez dans un instant.';

  // Écrit par l'application, donc déjà en français et plus précis que nous.
  if (semblePhraseFrancaise(texte)) return texte;

  return (
    'Une erreur inattendue s’est produite. Réessayez ; si elle revient, transmettez ' +
    `ce détail : « ${texte} ».`
  );
}
